/* eslint-disable no-console */
import { NAVIGATION_TARGETS, layoutForThirdPartyService, layoutService } from '@bedrock/fido-sdk';
import config from '@bedrock-rdk/config';
import { format } from 'date-fns';
import Algolia from '../../thirdParty/algolia/algolia.engine';
import {
  hasAlternativeContent,
  hasContent,
  shouldDisplayLanding,
  isKnownBlockTemplateType,
  isKnownSelectorTemplateType,
  isKnownTemplateType,
  prepareBlock,
  prepareAlternateContent,
  shouldDisplaySubscriptionExpiration,
  isProfilesGates,
  isPlayableEntity,
  prepareLastViewedProgress,
  shouldAutoSelectProfile,
  handleGroupOnPlatform,
  isSearchEntity,
  hasAlertView,
} from './layout.utils';
import {
  getRequestedEntityType,
  getRequestedEntityId,
  getCurrentSectionCode,
  getContentTemplatesOptions,
  getCurrentLayout,
  getLayoutById,
} from './layout.selectors';
import { getScreenSize, isAppInitialized } from '../app/app.selectors';
import { getNavigationV2, getNavigationV3 } from './layout.navigation.utils';
import {
  navigateToExpiredSubscriptionPage,
  navigateToLandingPage,
  navigateTo,
  APP_PATH,
  DEVICE_GATE_PATH,
} from '../../modules/routes/routes.utils';
import { showPopupAction } from '../popup/popup.actions';
import { PROFILES_GATE_POPUP_ID } from '../../modules/popups/popups.constants';
import { trackImpressions } from '../../modules/tornado/utils/impression';
import { PAGE_STATE } from '../../modules/tornado/utils/virtualization';
import { logout } from '../pairing/pairing.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { errorAction } from '../error/error.actions';
import { fetchScreenSaverData } from '../screenSaver/screenSaver.actions';
import { getLastViewedClipProgress, getLastViewedClipId } from '../player/player.selectors';
import { resetLastViewedData } from '../player/player.constants';
import { hideRouteSkeleton, setDeeplinkLocation } from '../routing/routing.actions';
import { completeAccount } from '../user/user.actions';
import { removeSplashScreen } from '../app/app.utils';
import { checkAccountCompletion } from './platform/default/accountCompletion';
import { autoSelectProfile } from './layout.profile.actions';
import { getBlockInfo } from './layout.reducer.utils';
import { getBffSearchFeature, getOpenCatalogFeature } from '../features/features.selectors';
import { getResultsAsBlocks, isAlgoliaRequest, mapSearchHits } from './layout.algolia.utils';

export { setCurrentSectionCode, SET_CURRENT_SECTION_CODE } from './layout.section.actions';

export const FETCH_NAVIGATION_SUCCESS = 'layout/FETCH_NAVIGATION_SUCCESS';
export const SET_CURRENT_ENTITY = 'layout/SET_CURRENT_ENTITY';
export const SET_CURRENT_ENTITY_ID = 'layout/SET_CURRENT_ENTITY_ID';
export const SET_CURRENT_ENTITY_TYPE = 'layout/SET_CURRENT_ENTITY_TYPE';
export const GENERATE_BLOCK_PLACEHOLDERS = 'layout/GENERATE_BLOCK_PLACEHOLDERS';
export const FETCH_BLOCK = 'layout/FETCH_BLOCK';
export const REPLACE_BLOCK = 'layout/REPLACE_BLOCK';
export const CLEAR_CACHE = 'layout/CLEAR_CACHE';
export const INVALIDATE_ITEM_CACHE = 'layout/INVALIDATE_ITEM_CACHE';
export const SET_REQUESTED_ENTITY = 'layout/SET_REQUESTED_ENTITY';
export const SET_CONTENT_TEMPLATES_OPTIONS = 'layout/SET_CONTENT_TEMPLATES_OPTIONS';
export const SET_NAVIGATION_ALLOWED = 'layout/SET_NAVIGATION_ALLOWED';
export const LOAD_NAVIGATION = 'layout/LOAD_NAVIGATION';

export const createSuccessActionType = entity => `layout/${entity}/FETCH_LAYOUT_SUCCESS`;

export const fetchedLayoutAction = (entity, data, id) => ({
  type: createSuccessActionType(entity),
  entity,
  data,
  id,
});

export const setContentTemplateOptions = contentTemplatesOptions => ({
  type: SET_CONTENT_TEMPLATES_OPTIONS,
  contentTemplatesOptions,
});

export const setRequestedEntity = (entityType, entityId) => ({
  type: SET_REQUESTED_ENTITY,
  entityType,
  entityId,
});

export const clearCache = (entityType, entityId, sectionCode) => ({
  type: CLEAR_CACHE,
  entityType,
  entityId,
  sectionCode,
});

export const invalidateItemCache = (itemIndex, blockIndex, item) => ({
  type: INVALIDATE_ITEM_CACHE,
  itemIndex,
  blockIndex,
  item,
});

export const setNavigationAllowed = isAllowed => ({
  type: SET_NAVIGATION_ALLOWED,
  isAllowed,
});

// Asks for the current section navigation without waiting for a layout, see the navigation middleware
export const loadNavigation = () => ({ type: LOAD_NAVIGATION });

// Section code -> request in flight, so concurrent triggers (e.g. profile infos and a layout arriving together) share it
const pendingNavigationRequests = new Map();

const requestNavigation = sectionCode => async dispatch => {
  // eslint-disable-next-line no-console
  console.log(`[layout.actions] Loading navigation for ${sectionCode}`);

  try {
    const data = await layoutForThirdPartyService.fetchNavigation({
      target: NAVIGATION_TARGETS.tv,
      sectionCode,
    });
    const navigation = Array.isArray(data) ? getNavigationV3(data) : getNavigationV2(data);

    return dispatch({
      type: FETCH_NAVIGATION_SUCCESS,
      sectionCode,
      navigation: {
        ...navigation,
        entries: navigation.entries.map(group => handleGroupOnPlatform(group)),
      },
    });
  } catch (error) {
    return dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.LAYOUT, error.message));
  }
};

export const fetchNavigation = section => (dispatch, getState) => {
  const sectionCode = section || getCurrentSectionCode(getState());

  if (!pendingNavigationRequests.has(sectionCode)) {
    const request = dispatch(requestNavigation(sectionCode)).finally(() => {
      pendingNavigationRequests.delete(sectionCode);
    });

    pendingNavigationRequests.set(sectionCode, request);
  }

  return pendingNavigationRequests.get(sectionCode);
};

export const fetchFromAlgolia = query => async (dispatch, getState) => {
  try {
    if (!Algolia.sdk) {
      await Algolia.init();
    }

    const algoliaResults = [];
    const state = getState();
    const screenSize = getScreenSize(state);
    const contentTemplatesOptions = getContentTemplatesOptions(state);
    const section = getCurrentSectionCode(state);

    const searchIndex = section === 'kids' ? 'kids' : 'default';
    const { results } = await Algolia.search(query, searchIndex);

    results.forEach(result => {
      const { hits = [], params = '' } = result;
      const facet = Algolia.extractFacetFromQuery(params);
      if (hits.length > 0 && facet) {
        algoliaResults.push(mapSearchHits(hits, facet, screenSize, contentTemplatesOptions));
      }
    });

    return algoliaResults;
  } catch (error) {
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PAIRING, error.message));

    return [];
  }
};

/**
 *
 * {@link https://integrateur-6play.m6web.fr/v2/openapi/index.html?urls.primaryName=layout}
 * @param {string} entityType The type of entity we are looking for (service, program, frontSpace, ...), see documentation linked
 * @param {string} entityId The id of the entity we are looking for. See documentation linked
 * @param {Object} params HTTP query params: pagination, search input, facets, etc.
 *
 * @returns {function}
 */
export const fetchLayout = (entityType, entityId, params) => async (dispatch, getState) => {
  const state = getState();

  if (!isAppInitialized(state)) {
    return false;
  }

  // Layout that was cached previously for the entity we are currently requesting
  const cachedLayout = getLayoutById(state, entityType, entityId);
  // This is a layout where we are BEFORE making the request
  const currentLayout = getCurrentLayout(state);
  if (currentLayout && currentLayout.entity && !isPlayableEntity(currentLayout.entity.type)) {
    trackImpressions(currentLayout);
  }

  const isPlayableEntityRequested = isPlayableEntity(entityType);
  const sectionCode = getCurrentSectionCode(state);
  const screenSize = getScreenSize(state);
  const contentTemplateOptions = getContentTemplatesOptions(state);
  const entity = {
    entityType,
    entityId,
    sectionCode,
    queryParameters: params
      ? {
          ...params,
          query: params.query && params.query.length >= config.search.charactersToSearch ? params.query : '',
        }
      : params,
  };

  if (isPlayableEntityRequested || hasAlertView(cachedLayout)) {
    dispatch(clearCache(entityType, entityId, sectionCode));
  }

  if (isSearchEntity(entityType, entityId) && !params) {
    const existing = getLayoutById(state, entityType, entityId);

    // Unique search screen behavior: it should keep it's query even if it was removed from navigation stack
    if (existing && existing.queryParameters) {
      entity.queryParameters = existing.queryParameters;
      params = existing.queryParameters;
    }
  }

  dispatch(setRequestedEntity(entityType, entityId));
  dispatch(fetchScreenSaverData(sectionCode));

  let data;
  try {
    const shouldUseAlgolia = !getBffSearchFeature(state);
    if (shouldUseAlgolia && isAlgoliaRequest(entity)) {
      const hits = await dispatch(fetchFromAlgolia(params.query));

      data = {
        analytics: {},
        blocks: getResultsAsBlocks(hits),
        entity: { type: entityType, id: entityId },
        layout: { section: sectionCode },
      };
    } else {
      data = await layoutForThirdPartyService.fetchByEntity(entity);
    }
  } catch (error) {
    return dispatch(
      errorAction(
        getCurrentLayout(getState()) ? ERROR_LEVELS.SILENT : ERROR_LEVELS.FATAL,
        ERROR_TYPES.LAYOUT,
        error.message,
      ),
    );
  } finally {
    removeSplashScreen();
  }

  if (shouldDisplaySubscriptionExpiration(data)) {
    return navigateToExpiredSubscriptionPage();
  }

  if (shouldDisplayLanding(data)) {
    // eslint-disable-next-line no-console
    console.log('[layout.actions] Landing layout received, logging out');

    if (getOpenCatalogFeature(state)) {
      dispatch(
        setDeeplinkLocation(isPlayableEntityRequested ? `${entityType}/${entityId}` : `app/${entityType}/${entityId}`),
      );

      navigateToLandingPage();

      return Promise.resolve();
    }

    return dispatch(logout()).finally(() => navigateToLandingPage());
  }

  if (data.redirection) {
    const target = `${APP_PATH}/${data.entity.type}/${data.entity.id}`;
    const { reasonCode } = data.redirection;

    if (reasonCode === 'device_to_revoke') {
      return dispatch(logout()).finally(() => navigateTo(target, false));
    }

    if (reasonCode === 'missing_entity') {
      return navigateTo(target, false);
    }

    if (reasonCode === 'devices_gate') {
      dispatch(setDeeplinkLocation(isPlayableEntity ? `${entityType}/${entityId}` : `app/${entityType}/${entityId}`));

      return navigateTo(DEVICE_GATE_PATH, false);
    }
  }

  // In case if need to replace some templates:
  // data.blocks.forEach(block => {
  //   if (block.blockTemplateId === 'List') {
  //     block.content.contentTemplateId = 'Square';
  //   }
  // });
  data.queryParameters = params
    ? Object.entries(params).reduce((result, [key, value]) => {
        const facet = key.match(/facet\[(.*)\]/)?.[1];
        if (!facet) {
          result[key] = value;

          return result;
        }

        // Dropping second-level facets that may still be selected on frontend side while discarded by backend
        if (data.facets.some(value => value.state && value.scope?.param === facet)) {
          result[key] = value;
        }

        return result;
      }, {})
    : params;
  data.blocks = data.blocks
    .filter(block => hasContent(block))
    .map(block => {
      if (!block.blockTemplateId && block.content.blockTemplateId) {
        block.blockTemplateId = block.content.blockTemplateId;
      }

      if (!block.templateId && block.content.contentTemplateId) {
        block.templateId = block.content.contentTemplateId;
      }

      return block;
    })
    .filter(
      block =>
        isKnownBlockTemplateType(block) &&
        (hasAlternativeContent(block) ? isKnownSelectorTemplateType(block) : true) &&
        isKnownTemplateType(block),
    )
    .map(prepareBlock(entity, screenSize, contentTemplateOptions))
    .map(prepareAlternateContent);

  if (entityType !== 'video' || entityType !== 'audio') {
    const lastViewedProgress = getLastViewedClipProgress(getState());
    const lastViewedId = getLastViewedClipId(getState());
    if (lastViewedProgress && lastViewedId) {
      prepareLastViewedProgress(data.blocks, lastViewedId, lastViewedProgress);

      dispatch(resetLastViewedData());
    }
  }

  if (isProfilesGates(data)) {
    if (shouldAutoSelectProfile(data, state)) {
      return dispatch(autoSelectProfile(data));
    }

    return dispatch(showPopupAction(PROFILES_GATE_POPUP_ID, { payload: data }));
  }

  // Preventing stacking of a new search results on top of old ones which will break pagination
  if (params) {
    dispatch(clearCache(entityType, entityId, sectionCode));
  }

  const result = dispatch(fetchedLayoutAction(entityType, data, entityId));

  dispatch(hideRouteSkeleton());

  if (isPlayableEntity) {
    dispatch(completeAccount());
  } else {
    dispatch(checkAccountCompletion());
  }

  return result;
};

/**
 * Redux thunk dispatching a pagination request for loading of a portion of items into a specified block
 *
 * @param {Object} options action options
 * @param {string} id block page identifier
 * @param {number} page page number, starts from 1
 * @param {string} parentId in case of pagination inside a selector block, represents an identifier of the parent-level block (i.e. Selector itself)
 * @returns {function} Redux action
 */
export const fetchBlockPage =
  ({ id: blockId, page, start, parentId = undefined, date = undefined }) =>
  async (dispatch, getState) => {
    const state = getState();
    const entityType = getRequestedEntityType(state);
    const entityId = getRequestedEntityId(state);
    const blockInfo = getBlockInfo(state.layout, blockId, parentId);
    if (!blockInfo) {
      return Promise.resolve();
    }
    // eslint-disable-next-line no-unused-vars
    const [_, existingBlock] = blockInfo;
    if (!existingBlock || !existingBlock.pages || existingBlock.pages[page - 1] !== PAGE_STATE.MISSING) {
      return Promise.resolve();
    }

    const sectionCode = getCurrentSectionCode(state);

    dispatch({
      type: GENERATE_BLOCK_PLACEHOLDERS,
      entityType,
      entityId,
      sectionCode,
      blockId,
      parentId,
      page,
      start,
    });

    const queryParameters = {};
    if (date) {
      queryParameters.day = format(date, 'yyyy-MM-dd');
    }

    let data;
    try {
      data = await layoutForThirdPartyService.fetchBlockPage({
        entityType,
        entityId,
        blockId,
        sectionCode,
        page,
        queryParameters,
      });
    } catch (error) {
      return dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.LAYOUT, error.message));
    }

    const screenSize = getScreenSize(state);
    const contentTemplateOptions = getContentTemplatesOptions(state);
    const entity = { entityType, entityId };
    const block = prepareBlock(entity, screenSize, contentTemplateOptions)(data);

    return dispatch({
      type: FETCH_BLOCK,
      pageIndex: page,
      blockId,
      parentId,
      totalItems: block.content.pagination ? block.content.pagination.totalItems : null,
      items: block.content.items,
    });
  };

export const fetchEPGLayout =
  ({ date, blockId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const sectionCode = getCurrentSectionCode(state);

    const day = format(date, 'yyyy-MM-dd');
    const data = await layoutService.fetchEpgGrid({
      sectionCode,
      queryParameters: {
        day,
      },
    });

    const entityType = getRequestedEntityType(state);
    const entityId = getRequestedEntityId(state);
    const entity = { entityType, entityId };
    const screenSize = getScreenSize(state);
    const contentTemplateOptions = getContentTemplatesOptions(state);
    const block = prepareBlock(entity, screenSize, contentTemplateOptions)(data);

    return dispatch({
      type: REPLACE_BLOCK,
      blockId,
      items: block.content.items,
      pagination: block.content.pagination,
      date: day,
    });
  };

export const toggleFacet = value => (dispatch, getState) => {
  const state = getState();
  const entityType = getRequestedEntityType(state);
  const entityId = getRequestedEntityId(state);
  const current = getCurrentLayout(state);
  const params = { ...(current?.queryParameters || {}) };
  const key = `facet[${value.scope.param}]`;

  if (value.state) {
    delete params[key];
  } else {
    params[key] = true;
  }

  return dispatch(fetchLayout(entityType, entityId, { ...params }));
};
